import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, featuredVideos } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeCategory]);

  const currentCategory = categories.find(c => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '全部视频';

  const filteredVideos = activeCategory === 'all'
    ? featuredVideos
    : featuredVideos.filter(v => v.category === activeCategory);

  const displayVideos = filteredVideos.length > 0
    ? filteredVideos
    : [...featuredVideos].sort(() => 0.5 - Math.random());

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '视频分类', href: '/category/all' },
                { label: categoryName },
              ]}
            />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mt-2 font-heading">
              {categoryName}
            </h1>
            {currentCategory?.description && (
              <p className="text-sm text-foreground-500 mt-2 max-w-xl">{currentCategory.description}</p>
            )}
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50 border-b border-background-200/60" aria-label="分类切换">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto no-scrollbar flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="py-10 md:py-14" aria-label={`${categoryName}视频列表`}>
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{displayVideos.length}</strong> 个视频
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="true">
              {displayVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
            {displayVideos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-video-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关视频，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}